import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import type { Channel as StreamChannelType } from "stream-chat";
import {
  Chat,
  Streami18n,
  Channel,
  MessageInput,
  Window,
  useChatContext,
} from "stream-chat-react";
import "stream-chat-react/dist/css/v2/index.css";
import { ChevronLeft, MessageCircle } from "lucide-react";

import { useStreamChat } from "@/features/chat/context/StreamChatContext";
import { useAuth } from "@/features/auth/context/AuthContext";
import { ActiveCallBanner } from "@/features/call/components/ActiveCallBanner";
import { UserListModal } from "@/features/chat/pages/UserListModal";
import { ChannelSettingsModal } from "@/features/chat/pages/ChannelSettingsModal";
import { TranslationProvider } from "@/features/chat/components/MessageTranslation";
import { SafeAttachment } from "@/features/chat/components/SafeMediaAttachment";
import { TypingIndicator } from "@/features/chat/components/TypingIndicator";
import { RightSidebar } from "@/features/chat/components/RightSidebar";
import { ThreadDetector } from "@/features/chat/components/ThreadDetector";
import { ChatHeader } from "@/features/chat/components/ChatHeader";
import { ChannelListPanel } from "@/features/chat/components/ChannelListPanel";
import {
  CustomMessage,
  CustomMessageOptions,
  TranslatedMessageList,
} from "@/features/chat/components/ChatMessage";
import {
  SharepointAttachmentSelector,
  SharepointSendButton,
} from "@/features/chat/components/SharepointComposer";
import type { PendingSharepointAttachment } from "@/features/chat/components/SharepointComposer";
import { useChatCalls } from "@/features/chat/hooks/useChatCalls";
import { useCreateTaskFromMessage } from "@/features/chat/hooks/useCreateTaskFromMessage";
import {
  STREAM_NATIVE_LANGS,
  STREAM_TRANSLATIONS,
} from "@/features/chat/constants/streamI18n";
import { TaskFormModal } from "@/features/tasks/components/TaskFormModal";
import { createSubtask } from "@/features/tasks/services/taskService";
import type { Task } from "@/features/tasks/types/task";

// Inner component that reads from ChatContext
const ChatInner = () => {
  const { t } = useTranslation();
  const { channel, setActiveChannel, client } = useChatContext();
  const { userData } = useAuth();

  const [showUserList, setShowUserList] = useState<"direct" | "group" | null>(
    null,
  );
  const [showSettings, setShowSettings] = useState(false);
  const [mobileShowChannel, setMobileShowChannel] = useState(false);
  // Key to force re-render of ChannelList when archive state changes
  const [channelListKey, setChannelListKey] = useState(0);
  // Guard: true while an archive operation is in-flight. Prevents the
  // <Channel> component from re-rendering (and looping) on SDK events
  // that arrive between setActiveChannel(undefined) and React's commit.
  const [isArchiving, _setIsArchiving] = useState(false);
  // Thread sidebar state
  const [threadOpen, setThreadOpen] = useState(false);
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [pendingSharepointAttachments, setPendingSharepointAttachments] =
    useState<PendingSharepointAttachment[]>([]);
  const [pendingMentionJump, setPendingMentionJump] = useState<{
    channelCid: string;
    messageId: string;
  } | null>(null);
  const mentionSelectionRequestRef = useRef(0);

  const isOfficer = ["officer", "admin"].includes(
    (userData?.userType ?? "").toLowerCase(),
  );

  // Group chat detection (>2 members)
  const isGroup = useMemo(
    () => Object.keys(channel?.state?.members || {}).length > 2,
    [channel],
  );

  const userId = userData?.id?.toString() || "";

  const bumpChannelList = useCallback(() => setChannelListKey((k) => k + 1), []);

  useEffect(() => {
    setPendingSharepointAttachments([]);
  }, [channel?.cid]);

  const { handleCallClick, handleJoinCall } = useChatCalls();
  const {
    createTaskSource,
    setCreateTaskSource,
    taskColumns,
    taskDefaultColumnId,
    handleCreateTaskFromMessage,
  } = useCreateTaskFromMessage();

  const handleArchiveChannel = useCallback(
    async (ch: StreamChannelType) => {
      if (!client.userID) return;
      try {
        // Deselect first if this is the active channel
        if (channel?.cid === ch.cid) {
          setActiveChannel(undefined);
          setMobileShowChannel(false);
        }
        await ch.updateMemberPartial(
          { set: { archived: true } },
          { userId: client.userID },
        );
        setChannelListKey((k) => k + 1);
      } catch (err) {
        console.error("Failed to archive channel:", err);
        // Restore on failure
        if (channel?.cid === ch.cid) setActiveChannel(ch);
      }
    },
    [client, channel, setActiveChannel],
  );

  const handlePinChannel = useCallback(async (ch: StreamChannelType) => {
    try {
      await (ch as any).pin();
      setChannelListKey((k) => k + 1);
    } catch (err) {
      console.error("Failed to pin channel:", err);
    }
  }, []);

  const handleUnpinChannel = useCallback(async (ch: StreamChannelType) => {
    try {
      await (ch as any).unpin();
      setChannelListKey((k) => k + 1);
    } catch (err) {
      console.error("Failed to unpin channel:", err);
    }
  }, []);

  const handleSelectArchivedChannel = useCallback(
    async (ch: StreamChannelType) => {
      try {
        if (!ch.initialized) {
          await ch.watch();
        }
        setActiveChannel(ch);
        setMobileShowChannel(true);
      } catch (err) {
        console.error("Failed to select archived channel:", err);
      }
    },
    [setActiveChannel],
  );

  const handleSelectMention = useCallback(
    async (ch: StreamChannelType, knownMessageId?: string) => {
      const requestId = ++mentionSelectionRequestRef.current;
      setPendingMentionJump(null);
      setActiveChannel(ch);
      setMobileShowChannel(true);

      let messageId = knownMessageId;
      if (!messageId) {
        try {
          const response = await client.search(
            { cid: ch.cid },
            { "mentioned_users.id": { $contains: userId } },
            { limit: 1, sort: { created_at: -1 } },
          );
          messageId = response.results[0]?.message.id;
        } catch (error) {
          console.warn("[Chat] Failed to find latest mention:", error);
        }
      }

      if (requestId === mentionSelectionRequestRef.current && messageId) {
        setPendingMentionJump({ channelCid: ch.cid, messageId });
      }
    },
    [client, setActiveChannel, userId],
  );

  const handleMentionJumpComplete = useCallback((messageId: string) => {
    setPendingMentionJump((current) =>
      current?.messageId === messageId ? null : current,
    );
  }, []);

  const handleChannelCreated = useCallback(
    async (newChannel: StreamChannelType) => {
      try {
        // Ensure the channel is watched before setting it active
        if (!newChannel.initialized) {
          await newChannel.watch();
        }
        setActiveChannel(newChannel);
      } catch (err) {
        console.error("Failed to activate channel:", err);
      }
      setShowUserList(null);
      setMobileShowChannel(true);
    },
    [setActiveChannel],
  );

  // When channel changes via ChannelList click, show the channel on mobile
  const handleChannelListSelect = useCallback(() => {
    setMobileShowChannel(true);
  }, []);

  return (
    <>
      {/* Channel List Sidebar */}
      <ChannelListPanel
        userId={userId}
        channelListKey={channelListKey}
        onRefreshList={bumpChannelList}
        mobileHidden={mobileShowChannel}
        onNewDirect={() => setShowUserList("direct")}
        onNewGroup={() => setShowUserList("group")}
        onPinChannel={handlePinChannel}
        onUnpinChannel={handleUnpinChannel}
        onArchiveChannel={handleArchiveChannel}
        onSelectArchived={handleSelectArchivedChannel}
        onSelectMention={handleSelectMention}
        onListClick={handleChannelListSelect}
      />

      {/* Message Area */}
      <div
        className={`flex-1 flex flex-col min-w-0 ${
          !mobileShowChannel ? "hidden md:flex" : "flex"
        }`}
      >
        <AnimatePresence mode="wait">
          {channel && !isArchiving ? (
            <motion.div
              key={channel.cid}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.2 }}
              className="flex-1 flex flex-col min-h-0"
            >
              <TranslationProvider
                autoTranslateEnabled={userData?.auto_translate_chat === true}
                translateExceptions={userData?.translate_exceptions ?? []}
              >
                <Channel
                  channel={channel}
                  Message={CustomMessage}
                  MessageOptions={CustomMessageOptions}
                  Attachment={SafeAttachment}
                  AttachmentSelector={() => (
                    <SharepointAttachmentSelector
                      channelId={channel?.id ?? channel?.cid ?? null}
                      pendingAttachments={pendingSharepointAttachments}
                      setPendingAttachments={setPendingSharepointAttachments}
                    />
                  )}
                  SendButton={(props) => (
                    <SharepointSendButton
                      {...props}
                      pendingAttachments={pendingSharepointAttachments}
                      setPendingAttachments={setPendingSharepointAttachments}
                    />
                  )}
                  MessageStatus={() => null}
                >
                  <ThreadDetector onThreadChange={setThreadOpen} />
                  <div className="flex flex-1 min-h-0">
                    <div className="flex-1 flex flex-col min-w-0">
                      {/* Mobile back button */}
                      <div className="md:hidden flex items-center px-2 py-1 border-b border-slate-200 bg-white">
                        <button
                          onClick={() => setMobileShowChannel(false)}
                          className="p-2.5 -ml-1 mr-1 hover:bg-slate-100 active:bg-slate-200 rounded-lg transition-colors"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                      </div>

                      <Window>
                        <ChatHeader
                          onCallClick={handleCallClick}
                          onSettingsClick={() => setShowSettings(true)}
                          rightPanelOpen={rightPanelOpen}
                          onToggleRightPanel={() =>
                            setRightPanelOpen(!rightPanelOpen)
                          }
                        />
                        <ActiveCallBanner
                          channel={channel}
                          onJoin={handleJoinCall}
                        />
                        <TranslatedMessageList
                          onCreateTask={isOfficer ? handleCreateTaskFromMessage : undefined}
                          jumpToMessageId={
                            pendingMentionJump?.channelCid === channel.cid
                              ? pendingMentionJump.messageId
                              : undefined
                          }
                          onJumpComplete={handleMentionJumpComplete}
                        />
                        <TypingIndicator />
                        <MessageInput focus maxRows={4} />
                      </Window>
                    </div>

                    {/* Right Sidebar — Thread or Members (desktop only) — MUST be inside <Channel> for <Thread> context */}
                    {rightPanelOpen && (
                      <RightSidebar
                        threadOpen={threadOpen}
                        onCloseThread={() => {
                          setThreadOpen(false);
                        }}
                        isGroup={isGroup}
                      />
                    )}
                  </div>
                </Channel>
              </TranslationProvider>
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex-1 flex items-center justify-center text-slate-400"
            >
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-indigo-50 flex items-center justify-center">
                  <MessageCircle className="w-8 h-8 text-indigo-300" />
                </div>
                <p className="text-lg">{t("chat.add_message")}</p>
                <p className="text-sm mt-1">{t("chat.select_conversation")}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* User List Modal */}
      {showUserList && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <UserListModal
              mode={showUserList}
              onClose={() => setShowUserList(null)}
              onChannelCreated={handleChannelCreated}
            />
          </motion.div>
        </AnimatePresence>
      )}

      {/* Channel Settings Modal */}
      {showSettings && channel && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ChannelSettingsModal
              channel={channel}
              onClose={() => setShowSettings(false)}
            />
          </motion.div>
        </AnimatePresence>
      )}

      {/* Create Task from Chat Message Modal */}
      <TaskFormModal
        isOpen={!!createTaskSource}
        onClose={() => setCreateTaskSource(null)}
        onSaved={async (created?: Task) => {
          // Auto-create a sub-task assigned to channel members when saving from chat
          if (created && createTaskSource?.channelMembers?.length) {
            try {
              await createSubtask(created.id, {
                title: created.title,
                description: created.description ?? undefined,
                deadline: created.deadline ?? undefined,
                assignee_ids: createTaskSource.channelMembers.map((m) => m.user_id),
              });
            } catch (err) {
              console.error("Failed to auto-create subtask from chat", err);
            }
          }
          setCreateTaskSource(null);
        }}
        columns={taskColumns}
        teams={[]}
        defaultColumnId={taskDefaultColumnId}
        sourceMessage={
          createTaskSource
            ? {
                messageId: createTaskSource.messageId,
                channelId: createTaskSource.channelId,
                text: createTaskSource.text,
                channelMembers: createTaskSource.channelMembers,
              }
            : undefined
        }
      />
    </>
  );
};

export const ChatPage = () => {
  const { chatClient, isReady } = useStreamChat();
  const { t, i18n } = useTranslation();

  const streamI18n = useMemo(() => {
    const lang = i18n.language?.split("-")[0] || "en";
    if (STREAM_NATIVE_LANGS.has(lang)) {
      return new Streami18n({ language: lang });
    }
    return new Streami18n({ language: "en", translationsForLanguage: STREAM_TRANSLATIONS[lang] || {} });
  }, [i18n.language]);

  if (!chatClient) {
    return (
      <div className="h-full flex items-center justify-center">
        {!isReady ? (
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        ) : (
          <div className="text-center text-slate-500">
            <p className="mb-2">{t("chat.chat_service_error")}</p>
            <p className="text-sm">{t("chat.chat_service_retry")}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="h-full flex -m-3 md:-m-6 -mb-20 md:-mb-6">
      <Chat client={chatClient} theme="str-chat__theme-light" i18nInstance={streamI18n}>
        <ChatInner />
      </Chat>
    </div>
  );
};
